import { useCallback, useEffect, useMemo, useRef, useState } from "react"

/** The TV player's `DEFAULT_ANIMATION_DELAY_MS`. */
const IDLE_DELAY_MS = 3000

/**
 * What the viewer is doing, which is what decides whether the chrome may hide.
 * Mirrors `apps/tv/src/features/video/controlsReducer.ts` minus the TV-only
 * focus states — the web player has a pointer, so `Interacting` and `Idle`
 * collapse into "is the chrome shown".
 */
export type PlayerMode = "watching" | "scrubbing" | "browsingSessions" | "editingSettings"

export interface ControlsVisibility {
  readonly mode: PlayerMode
  readonly isChromeVisible: boolean
  /** Any interaction: reveals the chrome and restarts the idle countdown. */
  readonly notifyActivity: () => void
  readonly beginScrub: () => void
  readonly endScrub: () => void
  readonly openSessions: () => void
  readonly closeSessions: () => void
  readonly toggleSessions: () => void
  readonly openSettings: () => void
  readonly closeSettings: () => void
  /** True when a panel or the session rail is open — Escape should close it first. */
  readonly hasOpenLayer: boolean
}

/**
 * Auto-hiding player chrome.
 *
 * The rule the TV player encodes and this keeps: the chrome only hides while the
 * video is actually playing and the viewer is only watching. Paused, scrubbing,
 * browsing sessions, or with a settings panel open, it stays up — hiding the
 * controls out from under an open menu is how a viewer loses the thing they were
 * halfway through choosing.
 */
export const useControlsVisibility = (isPlaying: boolean): ControlsVisibility => {
  const [mode, setMode] = useState<PlayerMode>("watching")
  const [isChromeVisible, setIsChromeVisible] = useState(true)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearIdleTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const canHide = isPlaying && mode === "watching"

  const notifyActivity = useCallback(() => {
    setIsChromeVisible(true)
  }, [])

  useEffect(() => {
    clearIdleTimer()

    if (!isChromeVisible || !canHide) {
      return
    }

    timeoutRef.current = setTimeout(() => setIsChromeVisible(false), IDLE_DELAY_MS)

    return clearIdleTimer
  }, [isChromeVisible, canHide, clearIdleTimer])

  // Pausing, opening a panel, or starting a scrub must bring the chrome back
  // even if the pointer never moved — a keyboard-only viewer never fires activity.
  useEffect(() => {
    if (!canHide) {
      setIsChromeVisible(true)
    }
  }, [canHide])

  useEffect(() => clearIdleTimer, [clearIdleTimer])

  return useMemo<ControlsVisibility>(() => {
    const enter = (next: PlayerMode) => () => {
      setIsChromeVisible(true)
      setMode(next)
    }

    const leave = (from: PlayerMode) => () => {
      setIsChromeVisible(true)
      setMode(current => (current === from ? "watching" : current))
    }

    return {
      mode,
      isChromeVisible,
      notifyActivity,
      beginScrub: enter("scrubbing"),
      endScrub: leave("scrubbing"),
      openSessions: enter("browsingSessions"),
      closeSessions: leave("browsingSessions"),
      toggleSessions: () => {
        setIsChromeVisible(true)
        setMode(current => (current === "browsingSessions" ? "watching" : "browsingSessions"))
      },
      openSettings: enter("editingSettings"),
      closeSettings: leave("editingSettings"),
      hasOpenLayer: mode === "browsingSessions" || mode === "editingSettings",
    }
  }, [mode, isChromeVisible, notifyActivity])
}
